/**
 * Streaming persona speech, shared by every surface that speaks.
 *
 * Today each caller POSTs `api/tts/persona`, waits for the WHOLE file, then
 * plays it. For a paragraph that is several seconds of silence before anything
 * is heard. `api/tts/stream` emits one `audio` event per finished sentence, so
 * playback starts on the first sentence while the rest is still being
 * synthesized.
 *
 * Contract (boss-dev-f9, 2026-10-11), `text/event-stream`:
 *   event: audio   data: { seq, text, mime: "audio/wav", audio_b64 }
 *   event: done    data: {}
 *   event: error   data: { message }
 *
 * Three levels of fallback, because voice must never be the reason a surface
 * looks broken:
 *   1. `api/tts/stream`               — streamed, the default
 *   2. `api/tts/persona`              — whole file, today's path, used when the
 *                                       stream route is absent (404/503) or
 *                                       fails BEFORE the first chunk plays
 *   3. the caller's own `catch`       — browser speechSynthesis, unchanged
 *
 * This THROWS when it could play nothing, so the existing
 * `catch { browserSpeak(clean) }` in each caller keeps working untouched.
 */

export interface PersonaVoiceRequest {
  text: string;
  surface: string;
  handle: string;
  displayName: string;
  title: string;
  /** Optional engine/voice hint; the server picks a default when absent. */
  voice?: string;
}

function authHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem('boss_token') ?? '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function base64ToBlob(b64: string, mime: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime || 'audio/wav' });
}

/**
 * One playback at a time per tab. A second ask cancels the first, which is
 * what the callers already did by hand with `audioRef.current?.pause()`.
 */
let active: { abort: AbortController; stop: () => void } | null = null;

export function stopPersonaSpeech(): void {
  active?.stop();
  active = null;
}

/**
 * A sink for synthesized clips, played strictly in arrival order.
 *
 * Exported because `/api/voice/answer` emits the same `audio` events as
 * `/api/tts/stream`, and both must share ONE player per tab — two utterances
 * talking over each other is worse than either delay this removes.
 */
export interface PersonaAudioSink {
  /** Queue one clip. `mime` comes from the event: edge-tts sends audio/mpeg, Gemini audio/wav. */
  push(audioB64: string, mime: string): void;
  /** No more clips are coming. */
  finish(): void;
  /** Stop now and release everything. */
  stop(): void;
  /** True once a clip has actually begun playing. Do not fail over after this. */
  hasStarted(): boolean;
  /** Resolves when every queued clip has played. */
  playing: Promise<void>;
}

/** Plays queued clips strictly in arrival order, pre-loading the next one. */
class ClipQueue {
  private urls: string[] = [];
  private playing = false;
  private ended = false;
  private current: HTMLAudioElement | null = null;
  private waiters: Array<() => void> = [];
  /** Set when a clip actually began playing: after this, do not fail over. */
  started = false;

  push(blob: Blob): void {
    this.urls.push(URL.createObjectURL(blob));
    this.wake();
  }

  finish(): void {
    this.ended = true;
    this.wake();
  }

  stop(): void {
    this.ended = true;
    this.current?.pause();
    this.current = null;
    for (const url of this.urls) URL.revokeObjectURL(url);
    this.urls = [];
    this.wake();
  }

  private wake(): void {
    const waiters = this.waiters;
    this.waiters = [];
    for (const resolve of waiters) resolve();
  }

  private nextArrival(): Promise<void> {
    return new Promise((resolve) => { this.waiters.push(resolve); });
  }

  /** Resolves when every clip pushed before finish() has been played. */
  async run(): Promise<void> {
    if (this.playing) return;
    this.playing = true;
    try {
      for (;;) {
        const url = this.urls.shift();
        if (!url) {
          if (this.ended) return;
          await this.nextArrival();
          continue;
        }
        const audio = new Audio(url);
        this.current = audio;
        try {
          await audio.play();
          this.started = true;
          await new Promise<void>((resolve) => {
            audio.onended = () => resolve();
            // A single bad clip must not strand the rest of the sentence queue.
            audio.onerror = () => resolve();
          });
        } finally {
          URL.revokeObjectURL(url);
          if (this.current === audio) this.current = null;
        }
      }
    } finally {
      this.playing = false;
    }
  }
}

export function createPersonaAudioSink(abort?: AbortController): PersonaAudioSink {
  stopPersonaSpeech();
  const controller = abort ?? new AbortController();
  const queue = new ClipQueue();
  active = { abort: controller, stop: () => { controller.abort(); queue.stop(); } };
  const playing = queue.run();
  return {
    push: (audioB64, mime) => queue.push(base64ToBlob(audioB64, mime)),
    finish: () => queue.finish(),
    stop: () => queue.stop(),
    hasStarted: () => queue.started,
    playing,
  };
}

/** Today's behaviour: one request, one file, play it. */
async function playWholeFile(req: PersonaVoiceRequest, abort: AbortController): Promise<void> {
  const res = await fetch('api/tts/persona', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(req),
    signal: abort.signal,
  });
  if (!res.ok) throw new Error(`TTS ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  const audio = new Audio(url);
  active = { abort, stop: () => { audio.pause(); URL.revokeObjectURL(url); } };
  try {
    await audio.play();
    await new Promise<void>((resolve) => {
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function speakPersona(req: PersonaVoiceRequest): Promise<void> {
  stopPersonaSpeech();
  const abort = new AbortController();
  const queue = new ClipQueue();
  active = { abort, stop: () => { abort.abort(); queue.stop(); } };

  let res: Response;
  try {
    res = await fetch('api/tts/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders() },
      body: JSON.stringify(req),
      signal: abort.signal,
    });
  } catch (err) {
    if (abort.signal.aborted) return;
    // Network-level failure: the old path may still answer.
    return playWholeFile(req, abort);
  }

  // 404 = not deployed yet, 503 = VOICE_FRONT/engine off. Both mean "use the
  // route that exists", not "the surface is broken".
  if (!res.ok || !res.body) {
    if (res.status === 404 || res.status === 503 || !res.body) return playWholeFile(req, abort);
    throw new Error(`TTS stream ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const player = queue.run();
  let streamError: string | null = null;
  let seen = -1;

  try {
    let buf = '';
    let finished = false;
    while (!finished) {
      const chunk = await reader.read();
      finished = chunk.done;
      if (chunk.value) buf += decoder.decode(chunk.value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let evType = '';
        let dataStr = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) evType = line.slice(6).trim();
          else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        let data: { seq?: number; mime?: string; audio_b64?: string; message?: string };
        try { data = JSON.parse(dataStr); } catch { continue; }

        if (evType === 'audio' && data.audio_b64) {
          // SSE rides one ordered connection, so arrival order IS sentence
          // order; `seq` is used only to drop a duplicate replay, never to
          // hold a clip back waiting for a gap that may never arrive.
          if (typeof data.seq === 'number') {
            if (data.seq <= seen) continue;
            seen = data.seq;
          }
          queue.push(base64ToBlob(data.audio_b64, data.mime ?? 'audio/wav'));
        } else if (evType === 'error') {
          streamError = data.message ?? 'voice stream failed';
          finished = true;
        } else if (evType === 'done') {
          finished = true;
        }
      }
    }
  } catch (err) {
    if (!abort.signal.aborted) streamError = err instanceof Error ? err.message : String(err);
  } finally {
    queue.finish();
  }

  await player;

  // Failing over AFTER audio has been heard would replay the opening sentence,
  // so once playback has started an error just ends the utterance.
  if (streamError && !queue.started && !abort.signal.aborted) {
    return playWholeFile(req, abort);
  }
  if (streamError && !queue.started) throw new Error(streamError);
}
